import { Typography, SxProps, Theme, useTheme } from '@mui/material';
import { ElementType, forwardRef, memo, useMemo } from 'react';
import { PossibleStatuses, getStatusStyles } from '../../styles/UsersStyles';

interface SubscriberStatusTextProps
  extends Omit<React.ComponentProps<typeof Typography>, 'ref' | 'component'> {
  status: PossibleStatuses;
  text: React.ReactNode;
  icon?: React.ReactNode;
  sx?: SxProps<Theme>;
  component?: ElementType;
}

export const StatusText = memo(
  forwardRef<HTMLElement, SubscriberStatusTextProps>(
    (
      {
        status,
        text,
        icon,
        sx,
        component = 'p',
        ...typographyProps
      }: SubscriberStatusTextProps,
      ref,
    ) => {
      const theme = useTheme();

      const memoStyles = useMemo(() => {
        return {
          ...(icon && {
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            '#iconspan': {
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            },
          }),
          ...getStatusStyles(theme, status),
          ...(sx ? (typeof sx === 'function' ? sx(theme) : sx) : {}),
        };
      }, [status, sx, icon, theme]);

      return (
        <Typography
          ref={ref}
          component={component}
          sx={memoStyles}
          {...typographyProps}
        >
          {icon && (
            <span
              id="iconspan"
              className="iconspan"
            >
              {icon}
            </span>
          )}
          {text}
        </Typography>
      );
    },
  ),
);
StatusText.displayName = 'StatusText';
